import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, Tab, ToggleButton } from 'react-bootstrap';
import TabsHeader from '../../components/Tabs/TabsHeader';
import { useTabs } from '../../hooks/use-tabs';
import { useApi } from '../../hooks/use-api';
import * as ROUTES from '../../constants/routes';
import CloseEducationModal from '../../components/Modal/CloseEducationModal';
import ConfirmModal from '../../components/Modal/ConfirmModal';
import { useToast } from '../../hooks/use-toast';
import Progress from './Progress';
import EducationOverview from '../../components/Tables/EmployeeDetails/Education/EducationOverview';

const KEY_PLANNING = 'planning';
const KEY_PROGRESS = 'progress';
function Educations({ employee, fetchEmployee, putEmployee }) {
	const { t } = useTranslation();
	const toast = useToast();
	const [toggleValue, setToggleValue] = useState(KEY_PLANNING);
	const [{ data, isLoading }] = useApi.get(ROUTES.getEmployeeAppointmentsPerEducation(employee.id));
	const { key, handleKeyChange } = useTabs(data ? data.map((item) => item.educationName) : []);
	const [showCloseEducationModal, setShowCloseEducationModal] = useState(false);
	const [showOpenEducationModal, setShowOpenEducationModal] = useState(false);

	if (isLoading) {
		return <div className="loading-spinner" />;
	}

	const handleOpenEducationSubmit = () => {
		putEmployee({ education: true })
			.then(() => {
				toast.addToast(t('education.updateSuccess'));
				fetchEmployee();
			})
			.catch(() => {
				toast.addToast(t('education.updateFail'), 'error');
			})
			.finally(() => setShowOpenEducationModal(false));
	};

	return data?.length ? (
		<>
			<CloseEducationModal
				show={showCloseEducationModal}
				setShow={setShowCloseEducationModal}
				fetchEmployee={fetchEmployee}
				employee={employee}
				putEmployee={putEmployee}
			/>
			<ConfirmModal
				show={showOpenEducationModal}
				setShow={setShowOpenEducationModal}
				title={t('education.open')}
				message={t('education.openModalConfirmText')}
				handleSubmit={handleOpenEducationSubmit}
			/>
			<Tab.Container
				id="employee-overview-tabs"
				activeKey={key}
				onSelect={(k) => handleKeyChange(k)}>
				<div className="mb-3 d-flex justify-content-between align-items-center">
					<TabsHeader
						options={{ variant: 'pills' }}
						tabKey={key}
						tabs={data
							.map((item) => item.educationName)
							.sort((educationName1, educationName2) =>
								educationName1.localeCompare(educationName2),
							)}
					/>
					{employee.education ? (
						<div>
							<ButtonGroup className="me-2">
								<ToggleButton
									id={KEY_PLANNING}
									value={KEY_PLANNING}
									type="radio"
									variant="outline-primary"
									checked={toggleValue === KEY_PLANNING}
									onChange={(e) => setToggleValue(e.target.value)}>
									{t('educations.planning')}
								</ToggleButton>
								<ToggleButton
									id={KEY_PROGRESS}
									value={KEY_PROGRESS}
									type="radio"
									variant="outline-primary"
									checked={toggleValue === KEY_PROGRESS}
									onChange={(e) => setToggleValue(e.target.value)}>
									{t('educations.progress')}
								</ToggleButton>
							</ButtonGroup>
							<Button
								variant="outline-primary"
								type="button"
								onClick={() => setShowCloseEducationModal(true)}>
								{t('education.close')}
							</Button>
						</div>
					) : (
						<Button
							variant="outline-primary"
							type="button"
							onClick={() => setShowOpenEducationModal(true)}>
							{t('education.open')}
						</Button>
					)}
				</div>
				<Tab.Content>
					{data.map((item) => (
						<Tab.Pane eventKey={item.educationName} key={item.educationName}>
							{toggleValue === 'planning' ? (
								<EducationOverview data={item.appointments} />
							) : (
								<Progress
									studentId={employee.id}
									dationInstanceId={item.dationInstanceId}
									educationId={item.educationId}
								/>
							)}
						</Tab.Pane>
					))}
				</Tab.Content>
			</Tab.Container>
		</>
	) : (
		<p className="mt-3 ms-3">{t('educations.no-planning')}</p>
	);
}

export default Educations;
